/**
 * Data-Driven Diamond Geometry Generator
 *
 * Creates a realistic brilliant cut diamond with 57-58 facets based on
 * actual measurements from CosmosDB/RAPNET.
 *
 * Brilliant Cut Anatomy:
 * - Table: 1 facet (top)
 * - Crown: 32 facets (8 bezel + 8 star + 16 upper girdle)
 * - Girdle: edge between crown and pavilion
 * - Pavilion: 24 facets (8 main + 16 lower girdle)
 * - Culet: 1 point (or small facet)
 *
 * Total: 57 facets (58 with culet facet)
 */
import * as THREE from 'three';
/**
 * Create brilliant cut diamond geometry from actual measurements
 */
export function createDataDrivenDiamond(proportions) {
    console.log('🔷 Creating data-driven diamond with proportions:', proportions);
    // Use provided values or GIA Excellent cut defaults
    const tablePercent = proportions.tablePercent || 56;
    const depthPercent = proportions.depthPercent || 61;
    const crownAngleDeg = proportions.crownAngle || 34.5;
    const pavilionAngleDeg = proportions.pavilionAngle || 40.8;
    console.log(`📊 Table: ${tablePercent}%, Depth: ${depthPercent}%, Crown: ${crownAngleDeg}°, Pavilion: ${pavilionAngleDeg}°`);
    // Normalize to unit diameter (girdle diameter = 2.0, radius = 1.0)
    const girdleRadius = 1.0;
    const tableSize = tablePercent / 100;
    const tableRadius = tableSize * girdleRadius;
    // Convert angles to radians
    const crownAngleRad = (crownAngleDeg * Math.PI) / 180;
    const pavilionAngleRad = (pavilionAngleDeg * Math.PI) / 180;
    // Calculate heights from angles and proportions
    // Crown height is based on the slope from table edge to girdle
    const crownHeight = proportions.crownHeight ||
        (girdleRadius - tableRadius) * Math.tan(crownAngleRad);
    // Total depth as a proportion of diameter (diameter = 2 * girdleRadius = 2)
    const totalDepth = (depthPercent / 100) * (2 * girdleRadius);
    const pavilionDepth = proportions.pavilionDepth || (totalDepth - crownHeight);
    console.log(`📏 Crown height: ${crownHeight.toFixed(3)}, Pavilion depth: ${pavilionDepth.toFixed(3)}, Total: ${(crownHeight + pavilionDepth).toFixed(3)}`);
    // Build geometry with proper BufferGeometry
    const vertices = [];
    const indices = [];
    let vertexIndex = 0;
    const addVertex = (x, y, z) => {
        vertices.push(x, y, z);
        return vertexIndex++;
    };
    const addTriangle = (a, b, c) => {
        indices.push(a, b, c);
    };
    // 8-fold radial symmetry (8 main sections for brilliant cut)
    const sections = 8;
    // ============================================================
    // CROWN VERTICES
    // ============================================================
    // 1. Table center (apex of crown)
    const tableCenter = addVertex(0, crownHeight, 0);
    // 2. Table edge vertices (8 points forming octagon)
    const tableEdge = [];
    for (let i = 0; i < sections; i++) {
        const angle = (i / sections) * Math.PI * 2;
        const x = Math.cos(angle) * tableRadius;
        const z = Math.sin(angle) * tableRadius;
        tableEdge.push(addVertex(x, crownHeight, z));
    }
    // 3. Star facet points (8 points between table and bezel)
    // These create the "star" pattern visible from the top
    const starPoints = [];
    const starRadius = tableRadius + (girdleRadius - tableRadius) * 0.4;
    const starHeight = crownHeight * 0.6;
    for (let i = 0; i < sections; i++) {
        const angle = (i / sections) * Math.PI * 2;
        const x = Math.cos(angle) * starRadius;
        const z = Math.sin(angle) * starRadius;
        starPoints.push(addVertex(x, starHeight, z));
    }
    // 4. Bezel break points (8 points)
    // Where the bezel facets meet the upper girdle facets
    const bezelBreak = [];
    const bezelRadius = tableRadius + (girdleRadius - tableRadius) * 0.7;
    const bezelHeight = crownHeight * 0.3;
    for (let i = 0; i < sections; i++) {
        const angle = (i / sections) * Math.PI * 2;
        const x = Math.cos(angle) * bezelRadius;
        const z = Math.sin(angle) * bezelRadius;
        bezelBreak.push(addVertex(x, bezelHeight, z));
    }
    // Girdle thickness (typically 1-3% of diameter)
    const girdleThickness = 0.02; // 2% thickness
    const girdleTop = girdleThickness / 2;
    const girdleBottom = -girdleThickness / 2;
    // 5. Girdle main points - TOP of girdle (8 points, aligned with main facets)
    const girdleMainTop = [];
    for (let i = 0; i < sections; i++) {
        const angle = (i / sections) * Math.PI * 2;
        const x = Math.cos(angle) * girdleRadius;
        const z = Math.sin(angle) * girdleRadius;
        girdleMainTop.push(addVertex(x, girdleTop, z));
    }
    // 6. Girdle offset points - TOP (8 points at y=girdleTop, between main points)
    const girdleOffsetTop = [];
    for (let i = 0; i < sections; i++) {
        const angle = ((i + 0.5) / sections) * Math.PI * 2;
        const x = Math.cos(angle) * girdleRadius;
        const z = Math.sin(angle) * girdleRadius;
        girdleOffsetTop.push(addVertex(x, girdleTop, z));
    }
    // 7. Girdle main points - BOTTOM of girdle (8 points)
    const girdleMainBottom = [];
    for (let i = 0; i < sections; i++) {
        const angle = (i / sections) * Math.PI * 2;
        const x = Math.cos(angle) * girdleRadius;
        const z = Math.sin(angle) * girdleRadius;
        girdleMainBottom.push(addVertex(x, girdleBottom, z));
    }
    // 8. Girdle offset points - BOTTOM (8 points)
    const girdleOffsetBottom = [];
    for (let i = 0; i < sections; i++) {
        const angle = ((i + 0.5) / sections) * Math.PI * 2;
        const x = Math.cos(angle) * girdleRadius;
        const z = Math.sin(angle) * girdleRadius;
        girdleOffsetBottom.push(addVertex(x, girdleBottom, z));
    }
    // ============================================================
    // PAVILION VERTICES
    // ============================================================
    // 9. Pavilion break points (8 points, where main pavilion facets break)
    const pavilionBreakDepth = -pavilionDepth * 0.43;
    const pavilionBreakRadius = girdleRadius * 0.55;
    const pavilionBreak = [];
    for (let i = 0; i < sections; i++) {
        const angle = (i / sections) * Math.PI * 2;
        const x = Math.cos(angle) * pavilionBreakRadius;
        const z = Math.sin(angle) * pavilionBreakRadius;
        pavilionBreak.push(addVertex(x, pavilionBreakDepth, z));
    }
    // 10. Lower girdle break points (8 points offset)
    const lowerGirdleBreak = [];
    for (let i = 0; i < sections; i++) {
        const angle = ((i + 0.5) / sections) * Math.PI * 2;
        const x = Math.cos(angle) * pavilionBreakRadius;
        const z = Math.sin(angle) * pavilionBreakRadius;
        lowerGirdleBreak.push(addVertex(x, pavilionBreakDepth, z));
    }
    // 11. Culet (bottom point or small facet)
    const culet = addVertex(0, -pavilionDepth, 0);
    console.log(`✅ Created ${vertexIndex} vertices`);
    // ============================================================
    // CREATE FACETS (TRIANGULAR FACES)
    // ============================================================
    let facetCount = 0;
    const facetMetadata = [];
    const addFacet = (type, section, v1, v2, v3) => {
        addTriangle(v1, v2, v3);
        // Store facet metadata
        facetMetadata.push({
            id: facetCount,
            type,
            section,
            vertices: [
                [vertices[v1 * 3], vertices[v1 * 3 + 1], vertices[v1 * 3 + 2]],
                [vertices[v2 * 3], vertices[v2 * 3 + 1], vertices[v2 * 3 + 2]],
                [vertices[v3 * 3], vertices[v3 * 3 + 1], vertices[v3 * 3 + 2]]
            ],
            normal: [0, 0, 0], // Will be computed later
            area: 0 // Will be computed later
        });
        facetCount++;
    };
    // TABLE FACETS (8 triangles from center to edge)
    // Viewed from above, vertices should go counter-clockwise for upward normal
    for (let i = 0; i < sections; i++) {
        const next = (i + 1) % sections;
        addFacet('table', i, tableCenter, tableEdge[next], tableEdge[i]);
    }
    // CROWN FACETS - Connect each ring to the next
    for (let i = 0; i < sections; i++) {
        const next = (i + 1) % sections;
        // Layer 1: Table edge → Star points (8 quads = 16 triangles)
        addFacet('star', i, tableEdge[i], starPoints[i], tableEdge[next]);
        addFacet('star', i, tableEdge[next], starPoints[i], starPoints[next]);
        // Layer 2: Star points → Bezel break (8 quads = 16 triangles)
        addFacet('bezel', i, starPoints[i], bezelBreak[i], starPoints[next]);
        addFacet('bezel', i, starPoints[next], bezelBreak[i], bezelBreak[next]);
        // Layer 3: Bezel break → Girdle top main (8 quads = 16 triangles)
        addFacet('upper-girdle', i, bezelBreak[i], girdleMainTop[i], bezelBreak[next]);
        addFacet('upper-girdle', i, bezelBreak[next], girdleMainTop[i], girdleMainTop[next]);
    }
    // GIRDLE BAND (the actual girdle thickness)
    for (let i = 0; i < sections; i++) {
        const next = (i + 1) % sections;
        // Girdle facets - vertical bands connecting top to bottom
        // Main girdle facets (8 rectangles = 16 triangles)
        // Each rectangle: top-left, bottom-left, top-right + bottom-left, bottom-right, top-right
        addFacet('upper-girdle', i, girdleMainTop[i], girdleMainTop[next], girdleMainBottom[i]);
        addFacet('upper-girdle', i, girdleMainTop[next], girdleMainBottom[next], girdleMainBottom[i]);
    }
    // PAVILION FACETS - Connect each ring to the next
    for (let i = 0; i < sections; i++) {
        const next = (i + 1) % sections;
        // Layer 1: Girdle bottom → Pavilion break (8 quads = 16 triangles)
        addFacet('pavilion-main', i, girdleMainBottom[i], pavilionBreak[i], girdleMainBottom[next]);
        addFacet('pavilion-main', i, girdleMainBottom[next], pavilionBreak[i], pavilionBreak[next]);
        // Layer 2: Pavilion break → Culet (8 triangles, converging to point)
        addFacet('culet', i, pavilionBreak[i], pavilionBreak[next], culet);
    }
    console.log(`✅ Created ${facetCount} facets (triangular faces)`);
    // Build BufferGeometry with non-indexed geometry for proper flat shading
    // For flat shading, each triangle needs its own vertices (not shared)
    const flatVertices = [];
    const flatNormals = [];
    for (let i = 0; i < indices.length; i += 3) {
        // Get the three vertex indices for this triangle
        const i1 = indices[i];
        const i2 = indices[i + 1];
        const i3 = indices[i + 2];
        // Get vertex positions
        const v1 = [vertices[i1 * 3], vertices[i1 * 3 + 1], vertices[i1 * 3 + 2]];
        const v2 = [vertices[i2 * 3], vertices[i2 * 3 + 1], vertices[i2 * 3 + 2]];
        const v3 = [vertices[i3 * 3], vertices[i3 * 3 + 1], vertices[i3 * 3 + 2]];
        // Add vertices
        flatVertices.push(...v1, ...v2, ...v3);
        // Compute face normal (cross product of two edges)
        const edge1 = [v2[0] - v1[0], v2[1] - v1[1], v2[2] - v1[2]];
        const edge2 = [v3[0] - v1[0], v3[1] - v1[1], v3[2] - v1[2]];
        let nx = edge1[1] * edge2[2] - edge1[2] * edge2[1];
        let ny = edge1[2] * edge2[0] - edge1[0] * edge2[2];
        let nz = edge1[0] * edge2[1] - edge1[1] * edge2[0];
        // Normalize the normal
        const length = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (length > 0) {
            nx /= length;
            ny /= length;
            nz /= length;
        }
        else {
            nx = 0;
            ny = 1;
            nz = 0; // Fallback
        }
        // Don't auto-correct normals - let the winding order determine direction
        // The material uses DoubleSide so both directions will render
        // Just keep the normal as computed from cross product
        // Add the same normal for all 3 vertices of this triangle (flat shading)
        flatNormals.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(flatVertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(flatNormals, 3));
    // Compute bounding sphere for proper rendering
    geometry.computeBoundingSphere();
    // Calculate normals and areas for each facet
    for (let i = 0; i < facetMetadata.length; i++) {
        const facet = facetMetadata[i];
        const [v1, v2, v3] = facet.vertices;
        // Calculate face normal
        const edge1 = [v2[0] - v1[0], v2[1] - v1[1], v2[2] - v1[2]];
        const edge2 = [v3[0] - v1[0], v3[1] - v1[1], v3[2] - v1[2]];
        const normal = [
            edge1[1] * edge2[2] - edge1[2] * edge2[1],
            edge1[2] * edge2[0] - edge1[0] * edge2[2],
            edge1[0] * edge2[1] - edge1[1] * edge2[0]
        ];
        const length = Math.sqrt(normal[0] * normal[0] + normal[1] * normal[1] + normal[2] * normal[2]);
        facet.normal = [normal[0] / length, normal[1] / length, normal[2] / length];
        // Calculate area (half magnitude of cross product)
        facet.area = length / 2;
    }
    // Store facet metadata in geometry userData
    geometry.userData.facets = facetMetadata;
    console.log(`💎 Diamond geometry complete: ${flatVertices.length / 3} unique vertices (3 per facet), ${indices.length / 3} triangles`);
    return geometry;
}
/**
 * Get diamond proportions summary for display
 */
export function getDiamondProportionsSummary(proportions) {
    const table = proportions.tablePercent || 56;
    const depth = proportions.depthPercent || 61;
    const crownAngle = proportions.crownAngle || 34.5;
    const pavilionAngle = proportions.pavilionAngle || 40.8;
    // Determine cut quality based on proportions
    const isExcellent = table >= 53 && table <= 58 &&
        depth >= 59 && depth <= 62.6 &&
        crownAngle >= 33.7 && crownAngle <= 35.8 &&
        pavilionAngle >= 40.6 && pavilionAngle <= 41.8;
    const quality = isExcellent ? 'Excellent' : 'Good';
    return `Table: ${table.toFixed(1)}%, Depth: ${depth.toFixed(1)}%, Crown: ${crownAngle.toFixed(1)}°, Pavilion: ${pavilionAngle.toFixed(1)}° (${quality})`;
}
/**
 * Validate if proportions are within GIA Excellent cut range
 */
export function isExcellentCut(proportions) {
    const table = proportions.tablePercent || 0;
    const depth = proportions.depthPercent || 0;
    const crownAngle = proportions.crownAngle || 0;
    const pavilionAngle = proportions.pavilionAngle || 0;
    return (table >= 53 && table <= 58 &&
        depth >= 59 && depth <= 62.6 &&
        crownAngle >= 33.7 && crownAngle <= 35.8 &&
        pavilionAngle >= 40.6 && pavilionAngle <= 41.8);
}
